/* eslint-disable no-nested-ternary */
import { TFunction } from "i18next";
import {
  GetAccount200ResponsePositionsInner,
  SideResponse,
} from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { MARK_PRICE_COLLAR } from "../../../constants/precision/form";
import { IMarketPrecisionGetter } from "../../../contexts/MarketInstrumentContext/types";
import { formatCurrencyWithSubscript } from "../../../utils/format";
import { Spinner } from "../Spinner";
import { EmptyTextContent, Highlight, TextContent } from "./style";
import { IPositionInfo, LiquidityTypeEnum } from "./types";

interface IRenderContentParams {
  pendingCloseFundingFee: number;
  fundingFeeDisplayValue: number;
  positionInfo: IPositionInfo | undefined;
  showOrderProtection: boolean;
  position: GetAccount200ResponsePositionsInner;
  getMarketPrecision: IMarketPrecisionGetter;
  t: TFunction;
}

export function renderContent({
  pendingCloseFundingFee,
  fundingFeeDisplayValue,
  positionInfo,
  showOrderProtection,
  position,
  getMarketPrecision,
  t,
}: IRenderContentParams) {
  const fundingFeeNode =
    pendingCloseFundingFee !== 0 ? (
      <>
        <br />
        <br />
        {t("funding_fee")}:{" "}
        <span
          style={{
            color:
              fundingFeeDisplayValue >= 0
                ? COLORS.up.one
                : COLORS.down.one,
          }}
        >
          {formatCurrencyWithSubscript(fundingFeeDisplayValue, {
            precision: getMarketPrecision(
              position.asset,
              position.instrument_type
            ).price_precision,
            showPositiveSign: true,
          })}
        </span>
      </>
    ) : null;

  if (!positionInfo) {
    return (
      <EmptyTextContent>
        <Spinner />
      </EmptyTextContent>
    );
  }

  if (showOrderProtection) {
    return (
      <TextContent>
        {t("order_protection_desc_1", {
          side: position.side === SideResponse.Buy ? t("bid") : t("ask"),
          markPercentage: MARK_PRICE_COLLAR * 100,
        })}
        <br />
        <br />
        {t("order_protection_desc_2")}
      </TextContent>
    );
  }

  switch (positionInfo.liquidityType) {
    case LiquidityTypeEnum.SUFFICIENT:
      return (
        <TextContent>
          {t("sufficient_liquidity_desc_1", {
            side: position.side === SideResponse.Buy ? t("bid") : t("offer"),
          })}{" "}
          <Highlight>
            {positionInfo.bestLiquidity.price}{" "}
            {positionInfo.bestLiquidity.iv
              ? `(${t("iv")} ${positionInfo.bestLiquidity.iv})`
              : ""}
          </Highlight>{" "}
          {t("sufficient_liquidity_desc_2")}{" "}
          <Highlight>
            {positionInfo.pnl >= 0 ? t("profit") : t("loss")}
          </Highlight>{" "}
          {t("of")}{" "}
          <span
            style={{
              color:
                positionInfo.pnl >= 0
                  ? COLORS.up.one
                  : COLORS.down.one,
            }}
          >
            {formatCurrencyWithSubscript(positionInfo.pnl, {
              precision: getMarketPrecision(
                position.asset,
                position.instrument_type
              ).price_precision,
              showPositiveSign: true,
            })}
          </span>
          {fundingFeeNode}
        </TextContent>
      );
    case LiquidityTypeEnum.NONE:
      return <TextContent>{t("no_liquidity_desc")}</TextContent>;
    default: {
      const marketPrecision = getMarketPrecision(
        position.asset,
        position.instrument_type
      );
      const formatAmount = (n: number) =>
        n.toFixed(marketPrecision.amount_precision);

      // INSUFFICIENT: show partial size + remaining-open info so the
      // realized PnL on the closed portion reconciles with the position's
      // overall unrealized PnL the user saw on the previous screen.
      if (
        positionInfo.liquidityType === LiquidityTypeEnum.INSUFFICIENT &&
        positionInfo.closedSize !== undefined &&
        positionInfo.totalSize !== undefined
      ) {
        return (
          <TextContent>
            {t("insufficient_blend_liquidity_desc")}
            <Highlight>{formatAmount(positionInfo.closedSize)}</Highlight>
            {t("insufficient_blend_of_total")}
            <Highlight>{formatAmount(positionInfo.totalSize)}</Highlight>
            {t("insufficient_blend_at_avg")}
            <Highlight>
              {positionInfo.averagePrice}
              {positionInfo.averageIV
                ? ` (${t("iv")} ${positionInfo.averageIV})`
                : ""}
            </Highlight>
            .
            <br />
            {t("insufficient_blend_realized")}{" "}
            <Highlight>
              {positionInfo.pnl >= 0 ? t("profit") : t("loss")}
            </Highlight>{" "}
            {t("insufficient_blend_on_closed")}
            <span
              style={{
                color:
                  positionInfo.pnl >= 0
                    ? COLORS.up.one
                    : COLORS.down.one,
              }}
            >
              {formatCurrencyWithSubscript(positionInfo.pnl, {
                precision: marketPrecision.price_precision,
                showPositiveSign: true,
              })}
            </span>
            .
            {positionInfo.remainingSize !== undefined &&
            positionInfo.remainingSize > 0 ? (
              <>
                <br />
                {t("insufficient_blend_remaining")}
                <Highlight>
                  {formatAmount(positionInfo.remainingSize)}
                </Highlight>
                {t("insufficient_blend_remaining_contracts")}
                {positionInfo.remainingUnrealizedPnl !== undefined ? (
                  <>
                    {t("insufficient_blend_remaining_unrealized")}
                    <span
                      style={{
                        color:
                          positionInfo.remainingUnrealizedPnl >= 0
                            ? COLORS.up.one
                            : COLORS.down.one,
                      }}
                    >
                      {formatCurrencyWithSubscript(
                        positionInfo.remainingUnrealizedPnl,
                        {
                          precision: marketPrecision.price_precision,
                          showPositiveSign: true,
                        }
                      )}
                    </span>
                    )
                  </>
                ) : null}
              </>
            ) : null}
            {fundingFeeNode}
          </TextContent>
        );
      }

      // BLEND: full close at a blended avg price across multiple levels —
      // pnl IS the full pnl; keep the existing concise copy.
      return (
        <TextContent>
          {t("sufficient_blend_liquidity_desc")}
          <Highlight>
            {positionInfo.averagePrice}{" "}
            {positionInfo.averageIV
              ? `(${t("iv")} ${positionInfo.averageIV})`
              : ""}
          </Highlight>{" "}
          {t("blend_liquidity_desc_2")}{" "}
          <Highlight>
            {positionInfo.pnl >= 0 ? t("profit") : t("loss")}
          </Highlight>{" "}
          {t("of")}{" "}
          <span
            style={{
              color:
                positionInfo.pnl >= 0
                  ? COLORS.up.one
                  : COLORS.down.one,
            }}
          >
            {formatCurrencyWithSubscript(positionInfo.pnl, {
              precision: marketPrecision.price_precision,
              showPositiveSign: true,
            })}
          </span>
          {fundingFeeNode}
        </TextContent>
      );
    }
  }
}
